import { Component, HostListener, inject, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { TranslatePipe } from '@ngx-translate/core';
import { UIStore } from '../../core/ui/ui.store';
import { AuthStore } from '../../core/login/services/auth.store';
import { envConfig } from '../../core/config/environment';
import { LoggerService } from '../../core/logging/logger.service';

// Define types for feedback form fields
type FeedbackType = 'general' | 'issue' | 'feature' | 'other';
// Define types for urgency levels and ratings
type UrgencyLevel = 'low' | 'medium' | 'high' | 'critical';
type Rating = 1 | 2 | 3 | 4 | 5 | null;

/**
 * Interface representing a selectable option in a dropdown menu, with a value and a translation key for localization.
 * - **value**: The actual value of the option, which can be of any type T.
 * - **translationKey**: A string key used for looking up the localized text for the option in translation files.
 */
interface SelectOption<T> {
  value: T;
  translationKey: string;
}

/**
 * Interface representing the structure of feedback data submitted by users.
 * - **type**: The type of feedback, which can be 'general', 'issue', 'feature', or 'other'.
 * - **topic**: A brief topic or subject of the feedback.
 * - **message**: The detailed message or content of the feedback.
 * - **urgency**: The urgency level of the feedback, which can be 'low', 'medium', 'high', 'critical', or an empty string if not applicable.
 * - **rating**: A numerical rating from 1 to 5, or null if not applicable.
 * - **sender_email**: The email address of the user submitting the feedback.
 */
export interface Feedback {
  type: FeedbackType;
  topic: string;
  message: string;
  urgency: UrgencyLevel | '';
  rating: Rating;
  sender_email: string;
}

/**
 * Enumeration representing the different states of feedback submission. It will be used to manage the UI state of the feedback form.
 * - **NOT_SUBMITTED**: The feedback form has not been submitted yet.
 * - **SUBMITTING**: The feedback form is currently being submitted.
 * - **SUBMITTED**: The feedback form has been successfully submitted.
 * - **ERROR**: An error occurred during the submission of the feedback form.
 */
enum SubmitStatus {
  NOT_SUBMITTED,
  SUBMITTING,
  SUBMITTED,
  ERROR,
}

@Component({
  selector: 'app-feedback',
  standalone: true,
  imports: [ReactiveFormsModule, TranslatePipe],
  templateUrl: './feedback.html',
  styleUrls: ['./feedback.css'],
})
export class FeedbackComponent {
  private readonly logger = inject(LoggerService);

  // Define the URL for submitting feedback, constructed using the base API URL from the environment configuration.
  readonly feedbackUrl = `${envConfig.apiUrl}/feedback`;

  /**
   * Array of available feedback types and urgency level, each represented as a select option with a value and a translation key.
   * If further feedback types are added in the future, they should be included in this array and the corresponding type definition should be updated accordingly.
   */
  readonly feedbackTypes: SelectOption<FeedbackType>[] = [
    { value: 'general', translationKey: 'FEEDBACK.FEEDBACK_TYPE.GENERAL' },
    { value: 'issue', translationKey: 'FEEDBACK.FEEDBACK_TYPE.ISSUE' },
    { value: 'feature', translationKey: 'FEEDBACK.FEEDBACK_TYPE.FEATURE' },
    { value: 'other', translationKey: 'FEEDBACK.FEEDBACK_TYPE.OTHER' },
  ];
  readonly urgencyLevels: SelectOption<UrgencyLevel>[] = [
    { value: 'low', translationKey: 'FEEDBACK.URGENCY_LEVEL.LOW' },
    { value: 'medium', translationKey: 'FEEDBACK.URGENCY_LEVEL.MEDIUM' },
    { value: 'high', translationKey: 'FEEDBACK.URGENCY_LEVEL.HIGH' },
    { value: 'critical', translationKey: 'FEEDBACK.URGENCY_LEVEL.CRITICAL' },
  ];

  readonly ratings = [1, 2, 3, 4, 5];

  uiStore = inject(UIStore);
  authStore = inject(AuthStore);
  private http = inject(HttpClient);

  // Signal to track the current submission status of the feedback form, initialized to NOT_SUBMITTED. Means the form just were opened
  submitStatus = signal(SubmitStatus.NOT_SUBMITTED);
  readonly submitStatusEnum = SubmitStatus;

  // Define the feedback form as a FormGroup with controls for type, topic, message, urgency, and rating. Each control has its own validators to ensure proper input.
  // Rating and urgency are conditionally required based on the selected feedback type, which is handled in the updateValidators method.
  feedbackForm = new FormGroup({
    type: new FormControl<FeedbackType>('general', {
      nonNullable: true,
      validators: Validators.required,
    }),
    topic: new FormControl('', {
      nonNullable: true,
      validators: Validators.required,
    }),
    message: new FormControl('', {
      nonNullable: true,
      validators: Validators.required,
    }),
    urgency: new FormControl<UrgencyLevel | ''>('', {
      nonNullable: true,
      }),
    rating: new FormControl<Rating>(null),
  });

  constructor() {
    this.updateValidators(this.feedbackForm.controls.type.value);
    this.feedbackForm.controls.type.valueChanges.subscribe(type => {
      this.logger.debug('FeedbackComponent', 'Feedback type changed', { type });
      this.updateValidators(type);
    });
  }
  ngOnInit(): void {
    this.logger.debug('FeedbackComponent', 'Feedback form opened');
  }
  // HostListener to listen for the Escape key press event on the document. 
  // When the Escape key is pressed, it checks if the feedback form is not currently submitting. If it's not submitting, it toggles the visibility of the feedback form using the UIStore service.
  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.submitStatus() !== SubmitStatus.SUBMITTING) {
      this.logger.debug('FeedbackComponent', 'Feedback dismissed with Escape');
      this.uiStore.toggleFeedbackFormVisibility();
    } else {
      this.logger.debug('FeedbackComponent', 'Dismissal blocked during submission');
    }
  }

  /**
   * Updates the validators for the urgency and rating controls based on the selected feedback type.
   * @param type The selected feedback type.
   */
  private updateValidators(type: FeedbackType): void {
    const { urgency, rating } = this.feedbackForm.controls;

    if (type === 'general') {
      urgency.clearValidators();
      urgency.setValue('');

      rating.setValidators([
        Validators.required,
        Validators.min(1),
        Validators.max(5),
      ]);
    } else {
      urgency.setValidators(Validators.required);

      rating.clearValidators();
      rating.setValue(null);
    }

    urgency.updateValueAndValidity();
    rating.updateValueAndValidity();
  }

  /**
   * Handles the submission of the feedback form.
   * @returns A promise that resolves when the submission is complete.
   */
  async onSubmit(): Promise<void> {
    if (this.feedbackForm.invalid) {
      this.logger.debug('FeedbackComponent', 'Submission blocked: invalid form');
      this.feedbackForm.markAllAsTouched();
      return;
    }

    const feedback: Feedback = {
      ...this.feedbackForm.getRawValue(),
      sender_email: this.authStore.username() ?? '',
    };

    try {
      this.submitStatus.set(SubmitStatus.SUBMITTING);
      this.logger.info('FeedbackComponent', 'Feedback submission started', { type: feedback.type });

      await firstValueFrom(
        this.http.post(this.feedbackUrl, feedback)
      );

      this.submitStatus.set(SubmitStatus.SUBMITTED);
      this.logger.info('FeedbackComponent', 'Feedback submitted successfully');

    } catch (error) {
      this.logger.error('FeedbackComponent', 'Feedback submission failed', error);
      this.submitStatus.set(SubmitStatus.ERROR);
    }
  }
}